import { matchPath } from 'react-router-dom';

/**
 * Maps route patterns to stable, human-readable screen names for the
 * "Page Viewed" event. Using a named screen (rather than the raw pathname)
 * keeps navigation analytics aggregatable — every visit to
 * /device/abc-123 and /device/def-456 rolls up under "Device Detail" — while
 * the raw path is still sent alongside for drill-down.
 *
 * Order matters: patterns are matched top-to-bottom and the first exact match
 * wins, so concrete paths (/device/claim) must precede param patterns that
 * would otherwise swallow them (/device/:deviceId).
 */
type ScreenRoute = { pattern: string; name: string };

const SCREEN_ROUTES: ScreenRoute[] = [
    { pattern: '/qr-proxy', name: 'QR Proxy' },

    // Auth
    { pattern: '/sign-in', name: 'Sign In' },
    { pattern: '/recover-password', name: 'Recover Password' },
    { pattern: '/reset-password', name: 'Reset Password' },
    { pattern: '/accept-invite', name: 'Accept Invite' },

    // Home / insights / profile
    { pattern: '/', name: 'Devices' },
    { pattern: '/insights', name: 'Insights' },
    { pattern: '/profile', name: 'Profile' },

    // Organization
    { pattern: '/organization/settings', name: 'Organization Settings' },
    { pattern: '/organization/members', name: 'Organization Members' },

    // Admin
    { pattern: '/admin/users', name: 'Admin Users' },
    { pattern: '/admin/organizations', name: 'Admin Organizations' },
    { pattern: '/admin/devices', name: 'Admin Devices' },

    // Marketing surfaces
    { pattern: '/marketplace', name: 'Marketplace' },
    { pattern: '/pricing', name: 'Pricing' },
    { pattern: '/calculator', name: 'Pricing Calculator' },
    { pattern: '/pitches', name: 'Pitches' },
    { pattern: '/demos', name: 'Demos' },

    // Locations
    { pattern: '/locations/new', name: 'New Location' },
    { pattern: '/locations', name: 'Locations' },
    { pattern: '/location/:locationId/edit', name: 'Edit Location' },
    { pattern: '/location/:locationId', name: 'Location Detail' },

    // Device tags
    { pattern: '/device-tags/new', name: 'New Device Tag' },
    { pattern: '/device-tags', name: 'Device Tags' },
    { pattern: '/device-tag/:deviceTagId', name: 'Device Tag Detail' },

    // Devices (concrete paths before param patterns)
    { pattern: '/devices/new', name: 'New Device' },
    { pattern: '/device/claim', name: 'Claim Device' },
    { pattern: '/device/:deviceId/claim', name: 'Claim Device' },
    { pattern: '/device/:deviceId/edit', name: 'Edit Device' },
    { pattern: '/device/:deviceId/config', name: 'Device Config' },
    { pattern: '/device/:deviceId/v2config', name: 'Device V2 Config' },
    { pattern: '/device/:deviceId', name: 'Device Detail' },

    // Playlists / studio
    { pattern: '/playlists', name: 'Playlists' },
];

export type ResolvedScreen = { screen: string; pattern: string };

/**
 * Resolves a pathname to its screen name and matched route pattern. Unknown
 * paths fall back to the raw pathname so nothing goes untracked.
 */
export function resolveScreen(pathname: string): ResolvedScreen {
    for (const route of SCREEN_ROUTES) {
        if (matchPath(route.pattern, pathname)) {
            return { screen: route.name, pattern: route.pattern };
        }
    }
    return { screen: pathname, pattern: pathname };
}
